// utils/cdUtils.js
import React from 'react';

export const capitalizeWords = (str) => {
  if (!str) return '';
  return str.replace(/\b\w/g, l => l.toUpperCase());
};

export const formatContent = (content) => {
  if (!content) return null;

  // First, let's clean and normalize the content
  const normalizedContent = normalizeContent(content);
  const lines = normalizedContent.split('\n');
  let formattedContent = [];
  let currentList = [];
  let inStakeholderSection = false;

  const renderList = (items) => {
    return (
      <ul className="list-disc pl-5 mb-6 space-y-2 text-white">
        {items.map((item, index) => (
          <li key={index} className="leading-relaxed">
            {processTextWithClickableLinks(item.replace(/^[-*•]\s+/, '').trim())}
          </li>
        ))}
      </ul>
    );
  };

  // Enhanced function to create clickable links and handle markdown bold formatting
  const processTextWithClickableLinks = (text) => {
    if (!text) return '';
    
    // Remove citation numbers in brackets at the end of lines AND anywhere in the text
    let cleanText = text.replace(/\[\d+(?:,\s*\d+)*\]/g, '').trim();
    
    // Handle markdown bold formatting (**text**)
    const boldRegex = /\*\*(.*?)\*\*/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = boldRegex.exec(cleanText)) !== null) {
      // Add text before the bold part
      if (match.index > lastIndex) {
        const beforeText = cleanText.substring(lastIndex, match.index);
        parts.push(...processUrlsInText(beforeText));
      }
      
      // Add the bold text
      parts.push(
        <strong key={`${match.index}`} className="text-white">
          {processUrlsInText(match[1])}
        </strong>
      );
      
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < cleanText.length) {
      parts.push(...processUrlsInText(cleanText.substring(lastIndex)));
    }

    return parts.length > 0 ? parts : processUrlsInText(cleanText);
  };

  // Function to find and make URLs clickable in regular text
  const processUrlsInText = (text) => {
    if (!text) return [''];
    
    const urlRegex = /(https?:\/\/[^\s)]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        // Clean the URL of any trailing punctuation
        const cleanUrl = part.replace(/[.,;!?]*$/, '');
        return (
          <a 
            key={`url-${index}`} 
            href={cleanUrl} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-orange-400 hover:text-orange-300 underline underline-offset-2 decoration-1 hover:decoration-2 transition-all duration-200"
          >
            {cleanUrl}
          </a>
        );
      }
      return part;
    });
  };

  const detectSectionHeader = (line) => {
    const cleanLine = line.trim();
    
    // Check for markdown headers (### or ##)
    if (cleanLine.match(/^#{1,4}\s+/)) {
      return {
        isHeader: true,
        level: (cleanLine.match(/^#+/) || [''])[0].length,
        title: cleanLine.replace(/^#+\s+/, '')
      };
    }
    
    // Check for specific section patterns in your data
    const sectionPatterns = [
      { pattern: /^(Business Overview|Company Overview|Overview)[\s:]*$/i, title: 'Business Overview' },
      { pattern: /^(Financials?|Financial Information|Financial Overview)[\s:]*$/i, title: 'Financials' },
      { pattern: /^(Pain Points?|Challenges?|Issues?)[\s:]*$/i, title: 'Pain Points' },
      { pattern: /^(Future Plans?|Roadmap|Strategy|Strategic Plans?)[\s:]*$/i, title: 'Future Plans' },
      { pattern: /^(Recent News|Latest News|News|Updates?)[\s:]*$/i, title: 'Recent News' },
      { pattern: /^(Individual Psychographic Profile|Stakeholder|Leadership|Stakeholders?)[\s:]*$/i, title: 'Stakeholder Insights' },
      { pattern: /^(Citations?)[\s:]*$/i, title: 'Citations' },
      { pattern: /^(Relevant Web Sources|Citations?|Sources?|References?)[\s:]*$/i, title: 'Citations ' },
      { pattern: /^(Work History|Professional Background|Background)[\s:]*$/i, title: 'Work History' },
      { pattern: /^(Leadership Style|Management Style|Style)[\s:]*$/i, title: 'Leadership Style' },
      { pattern: /^(Values|Core Values|Principles)[\s:]*$/i, title: 'Values' },
      { pattern: /^(Potential Objections|Objections|Concerns)[\s:]*$/i, title: 'Potential Objections' }
    ];
    
    for (const { pattern, title } of sectionPatterns) {
      if (pattern.test(cleanLine)) {
        return {
          isHeader: true,
          level: 2,
          title: title
        };
      }
    }
    
    // Check for bold text patterns that might be headers (like **Name**: Divyani Basantani)
    if (cleanLine.match(/^\*\*(.*?)\*\*:?\s*(.*)$/)) {
      const matches = cleanLine.match(/^\*\*(.*?)\*\*:?\s*(.*)$/);
      return {
        isHeader: true,
        level: 3,
        title: matches[1],
        subtitle: matches[2]
      };
    }
    
    return { isHeader: false };
  };

  lines.forEach((line, index) => {
    const sectionInfo = detectSectionHeader(line);
    
    if (sectionInfo.isHeader) {
      // Flush current list
      if (currentList.length > 0) {
        formattedContent.push(
          <tr key={`list-${index}`}>
            <td colSpan="2" className="px-4 py-2">
              {renderList(currentList)}
            </td>
          </tr>
        );
        currentList = [];
      }
      
      let displayTitle = sectionInfo.title;
      
      // Set section flags
      if (displayTitle.toLowerCase().includes('psychographic') || 
          displayTitle.toLowerCase().includes('stakeholder') ||
          displayTitle.toLowerCase() === 'name') {
        inStakeholderSection = true;
        if (displayTitle.toLowerCase() === 'name') {
          displayTitle = 'Stakeholder Profile';
        } else if (!displayTitle.includes('Stakeholder')) {
          displayTitle = 'Stakeholder Insights';
        }
      } else if (displayTitle.toLowerCase().includes('citation') || 
                 displayTitle.toLowerCase().includes('source') ||
                 displayTitle.toLowerCase().includes('web sources') ||
                 displayTitle.toLowerCase().includes('citations')) {
        displayTitle = 'Citations';
        inStakeholderSection = false;
      } else {
        if (sectionInfo.level <= 2) {
          inStakeholderSection = false;
        }
      }
      
      const headerClass = sectionInfo.level <= 2 ? 'text-xl' : 'text-lg';
      const fullTitle = sectionInfo.subtitle ? `${displayTitle}: ${sectionInfo.subtitle}` : displayTitle;
      
      // Updated section header with white background and navy blue text
      formattedContent.push(
        <tr key={`header-${index}`} className="w-full">
          <td colSpan="2" className="p-0 my-5">
            <div className="relative overflow-hidden rounded-md shadow-md my-5 bg-white">
              <div className="absolute inset-0 bg-white"></div>
              <div className="relative px-5 py-4 z-10">
                <span className={`text-slate-800 font-bold ${headerClass} font-playfair`}>
                  {fullTitle}
                </span>
              </div>
            </div>
          </td>
        </tr>
      );
    } 
    // Handle bullet points with more variations
    else if (line.trim().match(/^[-*•]\s+/) || line.trim().match(/^\d+\.\s+/)) {
      currentList.push(line);
    }
    // Handle regular content
    else if (line.trim() !== '') {
      if (currentList.length > 0) {
        formattedContent.push(
          <tr key={`list-${index}`}>
            <td colSpan="2" className="px-4 py-2">
              {renderList(currentList)}
            </td>
          </tr>
        );
        currentList = [];
      }
      
      // Process text with bold formatting and clickable links
      let processedText = line.trim();
      
      // Remove citation numbers anywhere in the text
      processedText = processedText.replace(/\[\d+(?:,\s*\d+)*\]/g, '').trim();
      
      formattedContent.push(
        <tr key={`text-${index}`}>
          <td colSpan={inStakeholderSection ? "1" : "2"} className="px-4 py-2 text-white leading-relaxed">
            {processTextWithClickableLinks(processedText)}
          </td>
          {inStakeholderSection && <td className="px-4 py-2"></td>}
        </tr>
      );
    }
  });

  // Handle any remaining list items
  if (currentList.length > 0) {
    formattedContent.push(
      <tr key="final-list">
        <td colSpan="2" className="px-4 py-2">
          {renderList(currentList)}
        </td>
      </tr>
    );
  }

  return formattedContent;
};

// Content normalization function - MUCH more conservative now
// utils/cdUtils.js - Updated normalizeContent function

export const normalizeContent = (content) => {
  if (!content) return '';
  
  let normalized = content;
  
  // Step 1: Remove <think> tags and their content if present (case insensitive)
  normalized = normalized.replace(/<think>[\s\S]*?<\/think>/gi, '');
  
  // Step 2: Handle potential code block wrapping
  normalized = normalized.trim();
  if (normalized.startsWith('```') && normalized.endsWith('```')) {
    const lines = normalized.split('\n');
    if (lines.length > 2 && lines[0].startsWith('```') && lines[lines.length - 1] === '```') {
      normalized = lines.slice(1, -1).join('\n');
    }
  }
  
  // Step 3: Handle JSON string wrapping
  if (normalized.startsWith('"') && normalized.endsWith('"')) {
    try {
      normalized = JSON.parse(normalized);
    } catch (e) {
      // If not valid JSON, just remove quotes
      normalized = normalized.slice(1, -1);
    }
  }
  
  // Step 4: Remove citation numbers anywhere in the text
  normalized = normalized.replace(/\[\d+(?:,\s*\d+)*\]/g, '');
  
  // Step 5: Fix markdown headers
  normalized = normalized.replace(/^(#{1,6})([^\s#])/gm, '$1 $2');
  normalized = normalized.replace(/^(#{1,6})\s*([^#\n]+?)\s*#{0,6}\s*$/gm, '$1 $2');
  
  // Step 6: Normalize section headers to consistent format
  const sectionMappings = {
    'BUSINESS OVERVIEW': '### Business Overview',
    'COMPANY OVERVIEW': '### Business Overview', 
    'OVERVIEW': '### Business Overview',
    'FINANCIALS': '### Financials',
    'FINANCIAL INFORMATION': '### Financials',
    'FINANCIAL OVERVIEW': '### Financials',
    'PAIN POINTS': '### Pain Points',
    'CHALLENGES': '### Pain Points',
    'ISSUES': '### Pain Points',
    'FUTURE PLANS': '### Future Plans',
    'ROADMAP': '### Future Plans',
    'STRATEGY': '### Future Plans',
    'STRATEGIC PLANS': '### Future Plans',
    'RECENT NEWS': '### Recent News',
    'LATEST NEWS': '### Recent News',
    'NEWS': '### Recent News',
    'UPDATES': '### Recent News',
    'INDIVIDUAL PSYCHOGRAPHIC PROFILE': '### Stakeholder Insights',
    'STAKEHOLDER INSIGHTS': '### Stakeholder Insights',
    'STAKEHOLDER': '### Stakeholder Insights',
    'LEADERSHIP': '### Stakeholder Insights',
    'STAKEHOLDERS': '### Stakeholder Insights',
    'RELEVANT WEB SOURCES': '### Citations',
    'CITATIONS': '### Citations',
    'SOURCES': '### Citations',
    'REFERENCES': '### Citations'
  };
  
  // Apply section mappings (case insensitive)
  Object.entries(sectionMappings).forEach(([oldHeader, newHeader]) => {
    const regex = new RegExp(`^(#+\\s*)?${oldHeader.replace(/\s+/g, '\\s+')}\\s*[:\\-]?\\s*$`, 'gmi');
    normalized = normalized.replace(regex, newHeader);
  });
  
  // Step 7: Normalize bullet points
  normalized = normalized.replace(/^[•·‣⁃]\s+/gm, '- ');
  normalized = normalized.replace(/^\*\s+/gm, '- ');
  
  // Step 8: Fix bold formatting
  normalized = normalized.replace(/\*\*\s+(.*?)\s+\*\*/g, '**$1**');
  
  // Step 9 & 10: Removed compound word fixes and CamelCase splitting
  // These were breaking URLs (e.g., marketfit.com → market fit.com)
  // Perplexity generates properly formatted text, so frontend fixes not needed
  
  // Step 11: Clean up whitespace
  normalized = normalized.replace(/\n{4,}/g, '\n\n\n');
  normalized = normalized.replace(/[ \t]+$/gm, ''); // Remove trailing spaces
  normalized = normalized.replace(/  +/g, ' '); // Multiple spaces to single space
  
  // Step 12: Ensure proper spacing around headers
  normalized = normalized.replace(/^(### .+)$/gm, '\n$1\n');
  
  // Step 13: Final cleanup
  normalized = normalized.replace(/\n{3,}/g, '\n\n');
  
  return normalized.trim();
};

// Utility function to detect if text is a section title
export const isSectionTitle = (text) => {
  return /^[A-Z\s]{4,30}$/.test(text.trim());
};

// Helper function to create better formatted HTML content
export const enhanceHtmlContent = (html) => {
  if (!html) return '';
  
  // Remove citation numbers anywhere in the text
  let enhanced = html.replace(/\[\d+(?:,\s*\d+)*\]/g, '');
  
  // Replace section headers with styled divs - updated to use white background and navy blue text
  enhanced = enhanced.replace(
    /<h3>(.*?)<\/h3>/gi, 
    '<div class="relative overflow-hidden rounded-md shadow-md my-5 bg-white"><div class="absolute inset-0 bg-white"></div><div class="relative px-5 py-4 z-10"><span class="text-slate-800 font-bold text-xl font-playfair">$1</span></div></div>'
  );
  
  // Replace subsection headers with styled headings - updated to use white background and navy blue text
  enhanced = enhanced.replace(
    /<h4>(.*?)<\/h4>/gi,
    '<div class="relative overflow-hidden rounded-md shadow-md my-3 bg-white"><div class="absolute inset-0 bg-white"></div><div class="relative px-5 py-3 z-10"><span class="text-slate-800 font-bold text-lg font-playfair">$1</span></div></div>'
  );
  
  // Enhance bullet points
  enhanced = enhanced.replace(
    /<ul>(.*?)<\/ul>/gs,
    '<ul class="list-disc pl-5 mb-6 space-y-2 text-white">$1</ul>'
  );
  
  enhanced = enhanced.replace(
    /<li>(.*?)<\/li>/gi,
    '<li class="leading-relaxed">$1</li>'
  );
  
  // Enhance paragraphs
  enhanced = enhanced.replace(
    /<p>(.*?)<\/p>/gi,
    '<p class="px-4 py-2 text-white leading-relaxed">$1</p>'
  );
  
  // Enhance links
  enhanced = enhanced.replace(
    /<a(.*?)>(.*?)<\/a>/gi,
    '<a$1 class="text-orange-400 hover:text-orange-300 underline underline-offset-2 decoration-1 hover:decoration-2 transition-all duration-200">$2</a>'
  );
  
  return enhanced;
};

// Function to generate a table of contents
export const generateTableOfContents = (content) => {
  if (!content) return [];
  
  const lines = content.split('\n');
  const toc = [];
  
  lines.forEach(line => {
    if (line.startsWith('### ') || line.startsWith('## ')) {
      const title = line.replace(/^###?\s+/, '').trim();
      const id = title.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
      
      toc.push({
        title,
        id,
        level: line.startsWith('## ') ? 1 : 2
      });
    }
  });
  
  return toc;
};

// Function to parse dates in a user-friendly format
export const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

// Function to extract the main domain from a URL
export const extractDomain = (url) => {
  if (!url) return '';
  try {
    const domain = url.replace(/^(?:https?:\/\/)?(?:www\.)?/i, "").split('/')[0];
    return domain;
  } catch (error) {
    return url;
  }
};

// Function to determine if a response is empty or contains "not available" placeholders
export const isEmptyResponse = (response) => {
  if (!response) return true;
  
  const lowerResponse = response.toLowerCase();
  const emptyPhrases = [
    'no specific information',
    'information not available',
    'no data available',
    'insufficient information',
    'not specified',
    'no information found'
  ];
  
  return emptyPhrases.some(phrase => lowerResponse.includes(phrase));
};